import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Textarea,
} from '@mui/joy';
import {
  Search as SearchIcon,
  KeyboardArrowRight,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WarningRounded,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import { getLocations, updateLocation, deleteLocation, createLocation } from '../../main/API/location';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { getLocationDisplayName } from '../../main/utils/locationDisplayName';
import { resolveAppLanguage } from '../../../i18n/language';
import { translateApiErrorData, translateAxiosErrorMessage, translateAggregateBackendMessage } from '../../main/utils/masterApiError';

export default function LocationsScreen() {
  const { t, i18n } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    country: '',
  });
  
  // Notification states
  const [failedNotification, setFailedNotification] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    name_ar: '',
    address: '',
    city: '',
    country: '',
    note: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({
    name: '',
    name_ar: '',
  });

  // Create modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    name_ar: '',
    address: '',
    city: '',
    country: '',
    note: '',
  });
  const [createFormErrors, setCreateFormErrors] = useState({
    name: '',
    name_ar: '',
  });

  // Delete modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(null);

  // Validation function
  const validateLocationName = useCallback((name) => {
    if (!name || name.trim() === '') {
      return { valid: false, error: t('settingsPages.accounts.nameRequired') };
    }

    if (name.length > 255) {
      return { valid: false, error: t('settingsPages.locations.nameMax') };
    }

    return { valid: true };
  }, [t]);

  const validateLocationNameAr = useCallback((nameAr) => {
    if (!nameAr || nameAr.trim() === '') {
      return { valid: false, error: t('settingsPages.locations.nameArRequired') };
    }
    if (nameAr.length > 255) {
      return { valid: false, error: t('settingsPages.locations.nameArMax') };
    }
    return { valid: true };
  }, [t]);

  const loadLocations = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.LOCATIONS_LIST)) {
      setLocations([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        ...filters,
      };
      
      const response = await getLocations(params);
      if (response && response.data) {
        setData(response.data);
        setLocations(response.data.results || []);
      } else {
        setLocations([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load locations:', error);
      setResponseMessage(error.message || t('settingsPages.locations.loadFailed'));
      setFailedNotification(true);
      setLocations([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, paginationModel.page, paginationModel.pageSize, filters, t]);

  useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadLocations();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  // Create handlers
  const handleCreateClick = () => {
    setCreateForm({
      name: '',
      name_ar: '',
      address: '',
      city: '',
      country: '',
      note: '',
    });
    setCreateFormErrors({ name: '', name_ar: '' });
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!hasPermission(PERMISSIONS.LOCATIONS_CREATE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    // Clear previous errors
    setCreateFormErrors({ name: '', name_ar: '' });

    const nameValidation = validateLocationName(createForm.name);
    const nameArValidation = validateLocationNameAr(createForm.name_ar);
    const nextErrors = {};
    if (!nameValidation.valid) nextErrors.name = nameValidation.error;
    if (!nameArValidation.valid) nextErrors.name_ar = nameArValidation.error;
    if (Object.keys(nextErrors).length > 0) {
      setCreateFormErrors({ name: '', name_ar: '', ...nextErrors });
      return;
    }

    setLoading(true);
    try {
      await createLocation(createForm);
      
      setResponseMessage(t('settingsPages.locations.createdSuccess'));
      setSuccessNotification(true);
      setCreateModalOpen(false);
      setCreateForm({
        name: '',
        name_ar: '',
        address: '',
        city: '',
        country: '',
        note: '',
      });
      loadLocations();
    } catch (error) {
      console.error('Failed to create location:', error);
      
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        const fallback = t('settingsPages.locations.saveFailed');
        if (errorData && (errorData.name || errorData.name_ar)) {
          const nameError = errorData.name
            ? (Array.isArray(errorData.name) ? errorData.name[0] : errorData.name)
            : '';
          const nameArError = errorData.name_ar
            ? (Array.isArray(errorData.name_ar) ? errorData.name_ar[0] : errorData.name_ar)
            : '';
          setCreateFormErrors({
            name: translateAggregateBackendMessage(String(nameError), t),
            name_ar: translateAggregateBackendMessage(String(nameArError), t),
          });
        }
        setResponseMessage(translateApiErrorData(errorData, t) || fallback);
        setFailedNotification(true);
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.locations.saveFailed'));
        setFailedNotification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const handleEditClick = useCallback((location) => {
    setEditingLocation(location);
    setEditForm({
      name: location.name || '',
      name_ar: location.name_ar || '',
      address: location.address || '',
      city: location.city || '',
      country: location.country || '',
      note: location.note || '',
    });
    setEditFormErrors({ name: '', name_ar: '' });
    setEditModalOpen(true);
  }, []);

  const handleEditSubmit = async () => {
    if (!editingLocation) return;
    if (!hasPermission(PERMISSIONS.LOCATIONS_UPDATE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }

    setEditFormErrors({ name: '', name_ar: '' });

    const nameValidation = validateLocationName(editForm.name);
    const nameArValidation = validateLocationNameAr(editForm.name_ar);
    const nextErrors = {};
    if (!nameValidation.valid) nextErrors.name = nameValidation.error;
    if (!nameArValidation.valid) nextErrors.name_ar = nameArValidation.error;
    if (Object.keys(nextErrors).length > 0) {
      setEditFormErrors({ name: '', name_ar: '', ...nextErrors });
      return;
    }

    setLoading(true);
    try {
      await updateLocation(editingLocation.id, editForm);
      
      setResponseMessage(t('settingsPages.locations.updatedSuccess'));
      setSuccessNotification(true);
      setEditModalOpen(false);
      setEditingLocation(null);
      loadLocations();
    } catch (error) {
      console.error('Failed to update location:', error);
      
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        const fallback = t('settingsPages.locations.saveFailed');
        if (errorData && (errorData.name || errorData.name_ar)) {
          const nameError = errorData.name
            ? (Array.isArray(errorData.name) ? errorData.name[0] : errorData.name)
            : '';
          const nameArError = errorData.name_ar
            ? (Array.isArray(errorData.name_ar) ? errorData.name_ar[0] : errorData.name_ar)
            : '';
          setEditFormErrors({
            name: translateAggregateBackendMessage(String(nameError), t),
            name_ar: translateAggregateBackendMessage(String(nameArError), t),
          });
        }
        setResponseMessage(translateApiErrorData(errorData, t) || fallback);
        setFailedNotification(true);
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.locations.saveFailed'));
        setFailedNotification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = useCallback((location) => {
    setDeletingLocation(location);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deletingLocation) return;
    if (!hasPermission(PERMISSIONS.LOCATIONS_DELETE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    setLoading(true);
    try {
      await deleteLocation(deletingLocation.id);
      
      setResponseMessage(t('settingsPages.locations.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteModalOpen(false);
      setDeletingLocation(null);
      loadLocations();
    } catch (error) {
      console.error('Failed to delete location:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.locations.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = useCallback(
    (dateString) => {
      if (!dateString) return t('actions.na');
      try {
        const d = new Date(dateString);
        const time = d.toLocaleTimeString('en-US', {
          timeZone: 'Asia/Muscat',
          hour12: true,
          hour: 'numeric',
          minute: 'numeric',
        });
        return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${time}`;
      } catch (e) {
        return dateString;
      }
    },
    [t]
  );

  const columns = useMemo(() => {
    const showArColumn = resolveAppLanguage(i18n.language) !== 'ar';
    const cols = [
      { field: 'id', headerName: t('settingsPages.locations.colId'), flex: 1, minWidth: 80 },
      {
        field: 'name',
        headerName: t('settingsPages.locations.colName'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => getLocationDisplayName(params.row, i18n.language) || t('actions.na'),
      },
    ];
    if (showArColumn) {
      cols.push({
        field: 'name_ar',
        headerName: t('settingsPages.locations.colNameAr'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => params.row.name_ar || t('actions.na'),
      });
    }
    cols.push(
    {
        field: 'address',
        headerName: t('actions.address'),
        flex: 2.5,
        minWidth: 200,
        renderCell: (params) => params.row.address || t('actions.na'),
      },
      {
        field: 'city',
        headerName: t('widgets.location'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => params.row.city || t('actions.na'),
      },
      {
        field: 'country',
        headerName: t('receiverDetailsPage.colDestination'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => params.row.country || t('actions.na'),
      },
      {
        field: 'note',
        headerName: t('actions.notes'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => params.row.note || t('actions.na'),
      },
      {
        field: 'created_at',
        headerName: t('trackingEvent.colCreatedAt'),
        flex: 2,
        minWidth: 160,
        renderCell: (params) => formatDate(params.row.created_at),
      },
      {
        field: 'actions',
        headerName: t('settingsPages.locations.colActions'),
        flex: 1.5,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {hasPermission(PERMISSIONS.LOCATIONS_UPDATE) && (
              <Tooltip title={t('settingsPages.locations.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasPermission(PERMISSIONS.LOCATIONS_DELETE) && (
              <Tooltip title={t('settingsPages.locations.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(params.row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    );
    return cols;
  }, [t, i18n.language, hasPermission, formatDate, handleEditClick, handleDeleteClick]);

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/settings">
                  {t('settingsPages.locations.breadcrumbSettings')}
                </Link>
                <Link color="neutral" href="/settings">
                  {t('settingsPages.accounts.breadcrumbMaster')}
                </Link>
                <Typography level="body-md">{t('settingsPages.locations.breadcrumbCurrent')}</Typography>
              </Breadcrumbs>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography textAlign={"start"} level="h1">{t('settingsPages.locations.pageTitle')}</Typography>
                {hasPermission(PERMISSIONS.LOCATIONS_CREATE) && (
                  <Button
                    startDecorator={<LocationIcon />}
                    onClick={handleCreateClick}
                    color="primary"
                    variant="solid"
                  >
                    {t('settingsPages.locations.btnAdd')}
                  </Button>
                )}
              </Box>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.LOCATIONS_LIST]}>
              {/* Filters */}
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Input
                    placeholder={t('settingsPages.locations.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Input
                    placeholder={t('widgets.location')}
                    value={filters.city}
                    onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Input
                    placeholder={t('receiverDetailsPage.colDestination')}
                    value={filters.country}
                    onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Button type="submit" loading={loading} startDecorator={<SearchIcon />}>
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Table */}
              <Box>
                <DataGrid
                  rows={locations || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? data.count : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            {/* Create Modal */}
            <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '600px', lg: '700px' },
                  maxWidth: '700px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  overflow: 'auto',
                }}
              >
                <ModalClose />
                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: 2 }}>
                  {t('settingsPages.locations.modalCreateTitle')}
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!createFormErrors.name} sx={{ width: '100%' }}>
                          <FormLabel>{t('settingsPages.locations.labelName')} *</FormLabel>
                          <Input
                            value={createForm.name}
                            onChange={(e) => {
                              setCreateForm(prev => ({ ...prev, name: e.target.value }));
                              if (createFormErrors.name) {
                                setCreateFormErrors((prev) => ({ ...prev, name: '' }));
                              }
                            }}
                            error={!!createFormErrors.name}
                            size="md"
                          />
                          {createFormErrors.name && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {createFormErrors.name}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!createFormErrors.name_ar} sx={{ width: '100%' }}>
                          <FormLabel>{t('settingsPages.locations.labelNameAr')} *</FormLabel>
                          <Input
                            value={createForm.name_ar}
                            onChange={(e) => {
                              setCreateForm((prev) => ({ ...prev, name_ar: e.target.value }));
                              if (createFormErrors.name_ar) {
                                setCreateFormErrors((prev) => ({ ...prev, name_ar: '' }));
                              }
                            }}
                            error={!!createFormErrors.name_ar}
                            size="md"
                          />
                          {createFormErrors.name_ar && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {createFormErrors.name_ar}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('actions.address')}</FormLabel>
                          <Textarea
                            value={createForm.address}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, address: e.target.value }))}
                            minRows={3}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('widgets.location')}</FormLabel>
                          <Input
                            value={createForm.city}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, city: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('receiverDetailsPage.colDestination')}</FormLabel>
                          <Input
                            value={createForm.country}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, country: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('actions.notes')}</FormLabel>
                          <Textarea
                            value={createForm.note}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, note: e.target.value }))}
                            minRows={3}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ 
                  px: { xs: 2, sm: 3 }, 
                  py: 2,
                  gap: 1,
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  '& > button': {
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '100%', sm: '100px' }
                  }
                }}>
                  <Button 
                    variant="plain" 
                    color="neutral" 
                    onClick={() => setCreateModalOpen(false)}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {t('actions.cancel')}
                  </Button>
                  <PermissionGate permissions={[PERMISSIONS.LOCATIONS_CREATE]}>
                    <Button 
                      onClick={handleCreateSubmit} 
                      loading={loading}
                      fullWidth={{ xs: true, sm: false }}
                    >
                      {t('trackingEvent.createButton')}
                    </Button>
                  </PermissionGate>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Edit Modal */}
            <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '600px', lg: '700px' },
                  maxWidth: '700px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  overflow: 'auto',
                }}
              >
                <ModalClose />
                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: 2 }}>
                  {t('settingsPages.locations.modalEditTitle')}
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!editFormErrors.name} sx={{ width: '100%' }}>
                          <FormLabel>{t('settingsPages.locations.labelName')} *</FormLabel>
                          <Input
                            value={editForm.name}
                            onChange={(e) => {
                              setEditForm(prev => ({ ...prev, name: e.target.value }));
                              if (editFormErrors.name) {
                                setEditFormErrors((prev) => ({ ...prev, name: '' }));
                              }
                            }}
                            error={!!editFormErrors.name}
                            size="md"
                          />
                          {editFormErrors.name && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {editFormErrors.name}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!editFormErrors.name_ar} sx={{ width: '100%' }}>
                          <FormLabel>{t('settingsPages.locations.labelNameAr')} *</FormLabel>
                          <Input
                            value={editForm.name_ar}
                            onChange={(e) => {
                              setEditForm((prev) => ({ ...prev, name_ar: e.target.value }));
                              if (editFormErrors.name_ar) {
                                setEditFormErrors((prev) => ({ ...prev, name_ar: '' }));
                              }
                            }}
                            error={!!editFormErrors.name_ar}
                            size="md"
                          />
                          {editFormErrors.name_ar && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {editFormErrors.name_ar}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('actions.address')}</FormLabel>
                          <Textarea
                            value={editForm.address}
                            onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                            minRows={3}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('widgets.location')}</FormLabel>
                          <Input
                            value={editForm.city}
                            onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('receiverDetailsPage.colDestination')}</FormLabel>
                          <Input
                            value={editForm.country}
                            onChange={(e) => setEditForm(prev => ({ ...prev, country: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('actions.notes')}</FormLabel>
                          <Textarea
                            value={editForm.note}
                            onChange={(e) => setEditForm(prev => ({ ...prev, note: e.target.value }))}
                            minRows={3}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ 
                  px: { xs: 2, sm: 3 }, 
                  py: 2,
                  gap: 1,
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  '& > button': {
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '100%', sm: '100px' }
                  }
                }}>
                  <Button 
                    variant="plain" 
                    color="neutral" 
                    onClick={() => setEditModalOpen(false)}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {t('actions.cancel')}
                  </Button>
                  <PermissionGate permissions={[PERMISSIONS.LOCATIONS_UPDATE]}>
                    <Button 
                      onClick={handleEditSubmit} 
                      loading={loading}
                      fullWidth={{ xs: true, sm: false }}
                    >
                      {t('actions.save')}
                    </Button>
                  </PermissionGate>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <WarningRounded />
                  {t('settingsPages.locations.deleteTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {deletingLocation &&
                    t('settingsPages.locations.deleteConfirm', { name: deletingLocation.name })}
                </DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <PermissionGate permissions={[PERMISSIONS.LOCATIONS_DELETE]}>
                    <Button variant="solid" color="danger" onClick={handleDeleteConfirm} loading={loading}>
                      {t('actions.delete')}
                    </Button>
                  </PermissionGate>
                </DialogActions>
              </ModalDialog>
            </Modal>
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

